import React, { useState, useMemo } from 'react';
import { Device, DeviceAlert } from '../../types/deviceTelemetry';
import { deviceTelemetryService } from '../../services/deviceTelemetryService';

interface AlertsPanelProps {
  devices: Device[];
  onDeviceSelect: (device: Device) => void;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ devices, onDeviceSelect }) => {
  const [filterType, setFilterType] = useState<string>('all');
  const [filterAcknowledged, setFilterAcknowledged] = useState<string>('unacknowledged');
  const [sortBy, setSortBy] = useState<string>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [acknowledgingAlerts, setAcknowledgingAlerts] = useState<Set<string>>(new Set());

  // Get all alerts from all devices
  const allAlerts = useMemo(() => {
    const alerts: Array<DeviceAlert & { device: Device }> = [];
    
    devices.forEach(device => {
      device.alerts.forEach(alert => {
        alerts.push({ ...alert, device });
      });
    });

    return alerts;
  }, [devices]);

  // Filter and sort alerts
  const filteredAlerts = useMemo(() => {
    let filtered = allAlerts;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(alert => alert.type === filterType);
    }

    // Filter by acknowledged status
    if (filterAcknowledged === 'acknowledged') {
      filtered = filtered.filter(alert => alert.acknowledged);
    } else if (filterAcknowledged === 'unacknowledged') {
      filtered = filtered.filter(alert => !alert.acknowledged);
    }

    // Sort alerts
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortBy) {
        case 'timestamp':
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
          break;
        case 'type':
          // Sort by severity: critical > warning > info
          const severityOrder = { critical: 3, warning: 2, info: 1 };
          aVal = severityOrder[a.type as keyof typeof severityOrder];
          bVal = severityOrder[b.type as keyof typeof severityOrder];
          break;
        case 'device':
          aVal = a.device.deviceName;
          bVal = b.device.deviceName;
          break;
        case 'store':
          aVal = a.device.storeName;
          bVal = b.device.storeName;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allAlerts, filterType, filterAcknowledged, sortBy, sortDirection]);

  // Get alert statistics
  const alertStats = useMemo(() => {
    const stats = {
      total: allAlerts.length,
      critical: allAlerts.filter(alert => alert.type === 'critical').length,
      warning: allAlerts.filter(alert => alert.type === 'warning').length,
      info: allAlerts.filter(alert => alert.type === 'info').length,
      unacknowledged: allAlerts.filter(alert => !alert.acknowledged).length,
      acknowledged: allAlerts.filter(alert => alert.acknowledged).length
    };

    return stats;
  }, [allAlerts]);

  // Acknowledge alert
  const acknowledgeAlert = async (alert: DeviceAlert & { device: Device }) => {
    if (acknowledgingAlerts.has(alert.id)) return;

    try {
      setAcknowledgingAlerts(prev => new Set(prev.add(alert.id)));
      await deviceTelemetryService.acknowledgeAlert(alert.device.deviceId, alert.id);
      
      // Update local state (in a real app, this would trigger a refresh)
      alert.acknowledged = true;
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      window.alert('Failed to acknowledge alert: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setAcknowledgingAlerts(prev => {
        const newSet = new Set(prev);
        newSet.delete(alert.id);
        return newSet;
      });
    }
  };

  // Bulk acknowledge alerts
  const bulkAcknowledgeAlerts = async () => {
    const unacknowledgedAlerts = filteredAlerts.filter(alert => !alert.acknowledged);
    
    if (unacknowledgedAlerts.length === 0) {
      window.alert('No unacknowledged alerts to process');
      return;
    }

    if (!window.confirm(`Acknowledge ${unacknowledgedAlerts.length} alert(s)?`)) {
      return;
    }

    for (const alert of unacknowledgedAlerts) {
      try {
        await acknowledgeAlert(alert);
      } catch (error) {
        console.error(`Failed to acknowledge alert ${alert.id}:`, error);
      }
    }
  };

  // Get alert type badge
  const getAlertTypeBadge = (type: string) => {
    const typeConfig = {
      critical: { color: '#ef4444', text: 'Critical' },
      warning: { color: '#f97316', text: 'Warning' },
      info: { color: '#3b82f6', text: 'Info' }
    };

    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.info;
    
    return (
      <span className="alert-type-badge" style={{ backgroundColor: config.color }}>
        {config.text}
      </span>
    );
  };

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="alerts-panel">
      {/* Alert Statistics */}
      <div className="alert-stats">
        <div className="stat-card total">
          <div className="stat-value">{alertStats.total}</div>
          <div className="stat-label">Total Alerts</div>
        </div>
        <div className="stat-card critical">
          <div className="stat-value">{alertStats.critical}</div>
          <div className="stat-label">Critical</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-value">{alertStats.warning}</div>
          <div className="stat-label">Warning</div>
        </div>
        <div className="stat-card info">
          <div className="stat-value">{alertStats.info}</div>
          <div className="stat-label">Info</div>
        </div>
        <div className="stat-card unacknowledged">
          <div className="stat-value">{alertStats.unacknowledged}</div>
          <div className="stat-label">Unacknowledged</div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="alerts-controls">
        <div className="filter-controls">
          <div className="filter-group">
            <label>Alert Type:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="critical">Critical Only</option>
              <option value="warning">Warning Only</option>
              <option value="info">Info Only</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select value={filterAcknowledged} onChange={(e) => setFilterAcknowledged(e.target.value)}>
              <option value="all">All Alerts</option>
              <option value="unacknowledged">Unacknowledged</option>
              <option value="acknowledged">Acknowledged</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="timestamp">Time</option>
              <option value="type">Severity</option>
              <option value="device">Device</option>
              <option value="store">Store</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Order:</label>
            <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        <div className="bulk-actions">
          <button
            onClick={bulkAcknowledgeAlerts}
            className="bulk-acknowledge-button"
            disabled={filteredAlerts.filter(alert => !alert.acknowledged).length === 0}
          >
            Acknowledge All Visible ({filteredAlerts.filter(alert => !alert.acknowledged).length})
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        Showing {filteredAlerts.length} of {allAlerts.length} alert{allAlerts.length !== 1 ? 's' : ''}
      </div>

      {/* Alerts List */}
      <div className="alerts-list">
        {filteredAlerts.length === 0 ? (
          <div className="no-alerts">
            {allAlerts.length === 0 ? (
              <div>
                <h3>No Active Alerts</h3>
                <p>All devices are operating normally with no alerts.</p>
              </div>
            ) : (
              <div>
                <h3>No Alerts Match Filters</h3>
                <p>Try adjusting your filter criteria to see more alerts.</p>
              </div>
            )}
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={`${alert.device.deviceId}-${alert.id}`}
              className={`alert-card ${alert.type} ${alert.acknowledged ? 'acknowledged' : 'unacknowledged'}`}
            >
              <div className="alert-header">
                <div className="alert-type-section">
                  {getAlertTypeBadge(alert.type)}
                </div>
                
                <div className="alert-device-section">
                  <button
                    onClick={() => onDeviceSelect(alert.device)}
                    className="device-link"
                  >
                    {alert.device.deviceName}
                  </button>
                  <div className="device-location">
                    {alert.device.storeName} • {alert.device.enterpriseName}
                  </div>
                </div>

                <div className="alert-time-section">
                  <div className="alert-time">{formatTimeAgo(alert.timestamp)}</div>
                  <div className="alert-timestamp">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="alert-content">
                <h4 className="alert-title">{alert.title}</h4>
                <p className="alert-message">{alert.message}</p>
              </div>

              <div className="alert-actions">
                {alert.acknowledged ? (
                  <div className="alert-acknowledged-status">
                    Acknowledged
                    {alert.resolvedAt && (
                      <span className="resolved-time">
                        • Resolved {formatTimeAgo(alert.resolvedAt)}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => acknowledgeAlert(alert)}
                    disabled={acknowledgingAlerts.has(alert.id)}
                    className="acknowledge-button"
                  >
                    {acknowledgingAlerts.has(alert.id) ? 'Acknowledging...' : 'Acknowledge'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;